import { Box, IconButton, Stack, Typography } from "@mui/material";
import {
  Close,
  InsertDriveFileOutlined,
  PictureAsPdf,
} from "@mui/icons-material";
import { getUploadBaseUrl } from "../../process/processSlices";
import { WallAttachment } from "../types/wallTypes";

const fileUrl = (a: WallAttachment) => `${getUploadBaseUrl()}/${a.url}`;
const isImage = (a: WallAttachment) =>
  a.contentType?.startsWith("image/") ||
  /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(a.url);
const isPdf = (a: WallAttachment) =>
  a.contentType === "application/pdf" || /\.pdf$/i.test(a.url);
const displayName = (a: WallAttachment) =>
  a.name || a.url.split("/").pop() || "file";

type Props = {
  attachments: WallAttachment[];
  onRemove: (index: number) => void;
};

/** Removable preview of attachments queued in a composer. */
export default function AttachmentEditList({ attachments, onRemove }: Props) {
  if (!attachments.length) return null;
  return (
    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
      {attachments.map((a, i) =>
        isImage(a) ? (
          <Box
            key={`${a.url}-${i}`}
            sx={{
              position: "relative",
              width: 72,
              height: 72,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <Box
              component="img"
              src={fileUrl(a)}
              alt={displayName(a)}
              sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
            />
            <IconButton
              size="small"
              onClick={() => onRemove(i)}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                p: 0.25,
                bgcolor: "rgba(0,0,0,0.55)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0,0,0,0.75)" },
              }}
            >
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ) : (
          <Stack
            key={`${a.url}-${i}`}
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{
              px: 1,
              py: 0.5,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "divider",
              bgcolor: "action.hover",
              maxWidth: 220,
            }}
          >
            {isPdf(a) ? (
              <PictureAsPdf color="error" fontSize="small" />
            ) : (
              <InsertDriveFileOutlined color="action" fontSize="small" />
            )}
            <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {displayName(a)}
            </Typography>
            <IconButton size="small" onClick={() => onRemove(i)} sx={{ p: 0.25 }}>
              <Close sx={{ fontSize: 14 }} />
            </IconButton>
          </Stack>
        ),
      )}
    </Box>
  );
}
